import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import Seo from '../components/Seo';
import { 
  User, MapPin, Briefcase, GraduationCap, 
  CheckCircle, Download, ExternalLink, MessageCircle,
  Phone, Share2, Shield, Calendar, Award
} from 'lucide-react';

interface Experience {
  title: string;
  company: string;
  duration: string;
  description: string;
  country: string;
}

interface Education {
  level: string;
  institution?: string;
  year?: number;
}

interface CandidateProfile {
  id: string;
  name: string;
  city: string;
  photo_url: string;
  skills: string[];
  experience_years: number;
  experience_details: Experience[];
  profession?: string;
  professions?: string[];
  education_details?: Education[];
  online_profile_url?: string;
  has_passport?: boolean;
}

export default function PublicProfile() {
  const { id } = useParams<{ id: string }>();
  const [profile, setProfile] = useState<CandidateProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchProfile() {
      if (!id) return;
      try {
        const { data, error } = await supabase
          .from('candidates')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;
        setProfile(data);
      } catch (err: any) {
        console.error('Error fetching profile:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchProfile();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jobisa-navy"></div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Profile Not Found</h1>
        <p className="text-gray-600 mb-8 text-center max-w-md">
          The candidate profile you are looking for might have been moved or deleted.
        </p>
        <Link to="/" className="bg-jobisa-navy text-white px-6 py-3 rounded-xl font-bold">
          Go to Homepage
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <Seo
        title={`${profile.name} | Worker Profile`}
        description={`${profile.name} from ${profile.city || 'India'} on Jobisa. View work experience, skills, and profile details.`}
        path={`/profile/${profile.id}`}
        noindex={false}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Person',
          name: profile.name,
          homeLocation: profile.city || 'India',
          knowsAbout: profile.skills || [],
          url: `https://jobisa.in/profile/${profile.id}`,
        }}
      />
      {/* Header / Banner */}
      <div className="h-48 bg-jobisa-navy relative">
        <div className="absolute -bottom-16 left-1/2 -translate-x-1/2 w-32 h-32 rounded-3xl border-4 border-white overflow-hidden bg-white shadow-xl">
          {profile.photo_url ? (
            <img src={profile.photo_url} alt={profile.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100 text-3xl font-bold text-gray-400">
              {profile.name.charAt(0)}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 pt-20">
        {/* Basic Info */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 uppercase">{profile.name}</h1>
          <p className="text-lg text-jobisa-navy font-semibold mb-3">
            {profile.profession || (profile.professions && profile.professions[0]) || 'Professional Candidate'}
          </p>
          <div className="flex items-center justify-center gap-4 text-gray-500 text-sm">
            <div className="flex items-center gap-1">
              <MapPin size={16} />
              <span>{profile.city || 'India'}</span>
            </div>
            <div className="flex items-center gap-1">
              <Briefcase size={16} />
              <span>{profile.experience_years} Years Exp.</span>
            </div>
            {profile.has_passport && (
              <div className="flex items-center gap-1 text-green-600 font-medium">
                <Shield size={16} />
                <span>Passport Ready</span>
              </div>
            )}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 mb-8 flex flex-col sm:flex-row gap-4">
          <a 
            href="https://jobisa.in" 
            className="flex-1 bg-jobisa-yellow text-jobisa-navy h-14 rounded-xl flex items-center justify-center font-bold gap-2 hover:bg-yellow-400 transition-colors"
          >
            <Download size={20} />
            Hire on Jobisa App
          </a>
          <button 
            className="flex-1 border-2 border-gray-200 text-gray-700 h-14 rounded-xl flex items-center justify-center font-bold gap-2 hover:bg-gray-50 transition-colors"
            onClick={() => {
              if (navigator.share) {
                navigator.share({
                  title: `${profile.name}'s Profile`,
                  text: `Check out ${profile.name}'s professional profile on Jobisa.`,
                  url: window.location.href
                });
              }
            }}
          >
            <Share2 size={20} />
            Share Profile
          </button>
        </div>

        <div className="space-y-8">
          {/* Skills */}
          {profile.skills && profile.skills.length > 0 && (
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Award className="text-jobisa-yellow" />
                Expertise & Skills
              </h2>
              <div className="flex flex-wrap gap-2">
                {profile.skills.map(skill => (
                  <span key={skill} className="bg-white border border-gray-200 px-4 py-2 rounded-lg text-gray-700 text-sm font-medium">
                    {skill}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Experience */}
          {profile.experience_details && profile.experience_details.length > 0 && (
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Briefcase className="text-jobisa-yellow" />
                Work History
              </h2>
              <div className="space-y-4">
                {profile.experience_details.map((exp, idx) => (
                  <div key={idx} className="bg-white p-5 rounded-2xl border border-gray-100">
                    <h3 className="font-bold text-gray-900 text-lg">{exp.title}</h3>
                    <p className="text-jobisa-navy font-medium text-sm mb-2">{exp.company} • {exp.country}</p>
                    <p className="text-gray-500 text-xs mb-3 italic">{exp.duration}</p>
                    <p className="text-gray-600 text-sm leading-relaxed">{exp.description}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Education */}
          {profile.education_details && profile.education_details.length > 0 && (
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <GraduationCap className="text-jobisa-yellow" />
                Education
              </h2>
              <div className="space-y-3">
                {profile.education_details.map((edu, idx) => (
                  <div key={idx} className="bg-white p-4 rounded-xl border border-gray-100 flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-gray-800">{edu.level}</h3>
                      <p className="text-gray-500 text-sm">{edu.institution || 'Recognized Institute'}</p>
                    </div>
                    {edu.year && <span className="text-gray-400 text-sm">{edu.year}</span>}
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Footer info */}
        <div className="mt-16 text-center text-gray-400 text-xs">
          <p>© {new Date().getFullYear()} Jobisa Recruitment. All rights reserved.</p>
          <div className="flex justify-center gap-4 mt-2">
            <Link to="/privacy-policy" className="hover:text-jobisa-navy">Privacy Policy</Link>
            <Link to="/terms-and-conditions" className="hover:text-jobisa-navy">Terms & Conditions</Link>
            <Link to="/worker" className="hover:text-jobisa-navy">Worker Portal</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
